import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { RotatingLines } from "react-loader-spinner";
import Accordion from "react-bootstrap/Accordion";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  adminAllConsultation,
  adminAppointmentCounts,
  adminAppointmentsExport,
  adminAppointmentsPage,
  adminUpdateAppointmentById,
  getAllDocuments,
  getDoctorsByConsultationId,
  getMriDocuments,
} from "../../../config/service";
import moment from "moment";
import { Button, Dropdown, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import ViewDocument from "./ViewDocument";
import Comment from "./Comment";
import MeetingUpdate from "./MeetingUpdate";
import { useTranslation } from "react-i18next";
import OfferDateModal from "./OfferDate";
import ProfileDetails from "./ProfileDetails";
import MeetingLog from "./MeetingLog";
import Report from "./Report";
import MyDoctorUpload from "./myDoctorUpload";
import AdminIdfModal from "./AdminIDFModal";

const MEETING_STATUS_COLUMNS = [
  "Scheduled",
  "Created",
  "DatesOffered",
  "AdminCare",
  "Done",
  "NoShow",
  "Canceled",
];

const DEFAULT_PER_PAGE = 50;
const LAZY_MIN_ROWS = 5;
const SEARCH_DEBOUNCE_MS = 600;

const createEmptySearchFields = () => ({
  meetingId: "",
  scheduleDate: "",
  createdDate: "",
  clientName: "",
  physicianName: "",
  consultingField: "",
  meetingStatus: "",
  comments: "",
});

const createEmptyAppliedSearch = () => ({ searchFields: {}, search: "" });

const createPerStatusMap = (factory) =>
  MEETING_STATUS_COLUMNS.reduce((acc, statusType) => {
    acc[statusType] = factory();
    return acc;
  }, {});

const COLUMN_FIELD_TO_SEARCH_KEY = {
  "Meeting Id": "meetingId",
  "Schedule Date": "scheduleDate",
  "Created Date": "createdDate",
  "Client Name": "clientName",
  "Physician Name": "physicianName",
  consultingField: "consultingField",
  MeetingStatus: "meetingStatus",
  "Admin comments": "comments",
};

const meetingDraftSearchRef = {
  current: createPerStatusMap(createEmptySearchFields),
};
const meetingDateDraftRef = { current: createPerStatusMap(() => ({})) };
const meetingDateFilterInstancesRef = { current: {} };
// Fallback when AG Grid remounts filters without statusType props.
const focusedMeetingStatusRef = { current: "Scheduled" };
// Always-fresh search trigger — AG Grid filter instances keep stale filterParams.
const meetingColumnFilterSearchRef = { current: null };

const resolveFilterStatus = (statusType) =>
  MEETING_STATUS_COLUMNS.includes(statusType)
    ? statusType
    : focusedMeetingStatusRef.current;

const triggerColumnFilterSearch = (statusType) => {
  if (typeof meetingColumnFilterSearchRef.current !== "function") {
    return;
  }
  meetingColumnFilterSearchRef.current(resolveFilterStatus(statusType));
};

const getTextDraft = (statusType) => {
  if (!meetingDraftSearchRef.current[statusType]) {
    meetingDraftSearchRef.current[statusType] = createEmptySearchFields();
  }
  return meetingDraftSearchRef.current[statusType];
};

const setTextDraftValue = (statusType, searchField, rawValue) => {
  const resolvedStatus = resolveFilterStatus(statusType);
  if (!searchField || !MEETING_STATUS_COLUMNS.includes(resolvedStatus)) {
    return resolvedStatus;
  }
  getTextDraft(resolvedStatus)[searchField] = String(rawValue ?? "");
  return resolvedStatus;
};

const getDateDraft = (statusType) => {
  if (!meetingDateDraftRef.current[statusType]) {
    meetingDateDraftRef.current[statusType] = {};
  }
  return meetingDateDraftRef.current[statusType];
};

const dateInstanceKey = (statusType, columnField) =>
  `${statusType}::${columnField}`;

const DATE_COLUMN_FIELD_MAP = {
  "Schedule Date": "scheduleDate",
  "Created Date": "createdDate",
};

const DATE_FILTER_OPTIONS = [
  "equals",
  "greaterThan",
  "lessThan",
  "notEqual",
  "inRange",
  "blank",
  "notBlank",
];

const DATE_SEARCH_FIELD_DEFAULTS = {
  scheduleDate: "",
  createdDate: "",
  scheduleDateFrom: "",
  scheduleDateTo: "",
  scheduleDateOperator: "",
  createdDateFrom: "",
  createdDateTo: "",
  createdDateOperator: "",
};

const DATE_FILTER_OPTION_LABELS = {
  equals: "Equal",
  greaterThan: "Greater than",
  lessThan: "Less than",
  notEqual: "Not equal",
  inRange: "In range",
  blank: "Empty",
  notBlank: "Not empty",
};

const TEXT_FILTER_OPTIONS = [
  "contains",
  "notContains",
  "equals",
  "notEqual",
  "startsWith",
  "endsWith",
  "blank",
  "notBlank",
];
const TEXT_FILTER_OPTION_LABELS = {
  contains: "Contains",
  notContains: "Not Contains",
  equals: "Equal",
  notEqual: "Not Equal",
  startsWith: "Starts With",
  endsWith: "Ends With",
  blank: "Empty",
  notBlank: "Not Empty",
};

const formatFilterDate = (value) => {
  if (!value) {
    return "";
  }
  return moment(value).format("DD/MM/YYYY");
};

const extractDateSearchFields = (filterModel = {}) => {
  const searchFields = {};

  Object.entries(filterModel).forEach(([columnField, model]) => {
    const searchKey = DATE_COLUMN_FIELD_MAP[columnField];
    if (!searchKey || !model) {
      return;
    }

    // Accept AG Grid / draft models even if filterType was dropped on remount.
    if (model.filterType && model.filterType !== "date") {
      return;
    }

    const filterType = model.type || "equals";

    if (filterType === "blank" || filterType === "notBlank") {
      searchFields[`${searchKey}Operator`] = filterType;
      return;
    }

    if (filterType === "inRange") {
      if (model.dateFrom) {
        searchFields[`${searchKey}From`] = formatFilterDate(model.dateFrom);
      }
      if (model.dateTo) {
        searchFields[`${searchKey}To`] = formatFilterDate(model.dateTo);
      }
      return;
    }

    if (model.dateFrom) {
      searchFields[searchKey] = formatFilterDate(model.dateFrom);
      if (filterType !== "equals") {
        searchFields[`${searchKey}Operator`] = filterType;
      }
    }
  });

  return searchFields;
};

const extractTextSearchFields = (filterModel = {}) => {
  const searchFields = {};

  Object.entries(filterModel).forEach(([columnField, model]) => {
    const searchKey = COLUMN_FIELD_TO_SEARCH_KEY[columnField];
    if (!searchKey || !model || model.filterType === "date") {
      return;
    }

    const filterType = model.type || "contains";

    if (filterType === "blank" || filterType === "notBlank") {
      searchFields[`${searchKey}Operator`] = filterType;
      return;
    }

    if (model.filter != null && String(model.filter).trim()) {
      searchFields[searchKey] = String(model.filter);
      if (filterType !== "contains") {
        searchFields[`${searchKey}Operator`] = filterType;
      }
    }
  });

  return searchFields;
};

const getMergedFilterModel = (statusType) => {
  const merged = {};
  const api = gridApi[statusType];

  if (typeof api?.getFilterModel === "function") {
    Object.assign(merged, api.getFilterModel());
  }

  Object.entries(getDateDraft(statusType)).forEach(([columnField, model]) => {
    if (model) {
      merged[columnField] = model;
    }
  });

  return merged;
};

const flushDateFilterModels = (statusType) => {
  Object.entries(meetingDateFilterInstancesRef.current).forEach(
    ([key, instance]) => {
      if (statusType && !key.startsWith(`${statusType}::`)) {
        return;
      }
      if (typeof instance?.publishModel === "function") {
        instance.publishModel();
      }
    }
  );
};

const buildSearchFieldsFromFilterModel = (filterModel = {}, statusType) => {
  const mergedFilterModel = { ...filterModel };

  // Date drafts win over grid model — survives AG Grid column remounts.
  Object.entries(getDateDraft(statusType)).forEach(([columnField, model]) => {
    if (model) {
      mergedFilterModel[columnField] = model;
    }
  });

  const dateSearchFields = extractDateSearchFields(mergedFilterModel);
  const textSearchFields = extractTextSearchFields(mergedFilterModel);
  const textDraft = { ...getTextDraft(statusType) };

  // Never let empty date placeholders wipe a real scheduleDate/createdDate.
  Object.keys(DATE_SEARCH_FIELD_DEFAULTS).forEach((key) => {
    delete textDraft[key];
  });

  // Draft is source of truth for text filters. Empty draft clears stale grid model
  // (AG Grid may still report the old filter when the input was just erased).
  const mergedTextFields = { ...textSearchFields };
  Object.entries(textDraft).forEach(([key, value]) => {
    const trimmed = String(value ?? "").trim();
    if (trimmed) {
      mergedTextFields[key] = trimmed;
    } else {
      delete mergedTextFields[key];
      delete mergedTextFields[`${key}Operator`];
    }
  });

  return buildSearchFieldsPayload({
    ...mergedTextFields,
    ...dateSearchFields,
  });
};

const clearAllGridFilters = () => {
  Object.keys(gridApi).forEach((statusKey) => {
    gridApi[statusKey]?.setFilterModel(null);
  });
};

const createStatusState = () => ({
  rows: [],
  page: 0,
  total: 0,
  totalPages: 0,
  loading: false,
  loaded: false,
});

const createStatusDataMap = () =>
  MEETING_STATUS_COLUMNS.reduce((acc, type) => {
    acc[type] = createStatusState();
    return acc;
  }, {});

const buildSearchFieldsPayload = (searchFields = {}) =>
  Object.fromEntries(
    Object.entries(searchFields).filter(([, value]) =>
      String(value || "").trim()
    )
  );

const hasActiveSearch = (searchFields, text = "") => {
  return (
    Object.values(searchFields).some((value) => String(value || "").trim()) ||
    String(text || "").trim()
  );
};

const formatMeetingRow = (d, lang) => ({
  "Meeting Id": d._id,
  "Created Date": new Date(d.createdAt),
  "Schedule Date": d?.scheduledDate ? new Date(d?.scheduledDate) : null,
  "Client Name": `${d.clientData?.first_name ? d?.clientData?.first_name : ""} ${d.clientData?.last_name ? d?.clientData?.last_name : ""
    }`,
  "Physician Name":
    d?.doctorData?.first_name && d?.doctorData?.first_name
      ? `${d?.doctorData?.first_name ? d?.doctorData?.first_name : ""} ${d?.doctorData?.last_name ? d?.doctorData?.last_name : ""
      }`
      : d?.offerDates?.length != 0 &&
        d?.offerDates[0]?.doctorUserDetails &&
        d?.statuses !== "Scheduled"
        ? `${d?.offerDates[0]?.doctorUserDetails[0]?.first_name} ${d?.offerDates[0]?.doctorUserDetails[0]?.last_name}`
        : null,
  consultingField: lang === "EN" ? d?.consultation?.eng : d?.consultation?.heb,
  MeetingStatus: d.statuses,
  Comments: d.comments,
  ClientId: d.ClientId,
  data: d,
  "Request Description": d?.requestDescription,
  Age: ageCalculator(d?.meetingpatientdetails?.patientDOB),
  Email: d?.meetingpatientdetails?.patientEmail,
  "Client Id": d?.ClientId,
  DOB: moment(d?.meetingpatientdetails?.patientDOB).format("DD-MM-YYYY"),
  Gender: d?.clientData?.clientGender,
  Phone: d?.meetingpatientdetails?.patientPhone,
  "Marital Status": d?.meetingpatientdetails?.patientMaritalStatus,
  Address: d?.clientData?.clientAddress?.country,
});

const formatStatusRows = (response, lang) => {
  const result = response?.data?.result;
  return Array.isArray(result)
    ? result.map((row) => formatMeetingRow(row, lang))
    : [];
};

class BackendDateColumnFilter {
  init(params) {
    this.filterChangedCallback = params.filterChangedCallback;
    this.onEnter = params.filterParams?.onEnter;
    this.onLiveSearch = params.filterParams?.onLiveSearch;
    this.statusType = params.filterParams?.statusType;
    this.columnField = params.colDef.field;
    this.api = params.api;
    this.model = null;

    if (this.statusType && this.columnField) {
      meetingDateFilterInstancesRef.current[
        dateInstanceKey(this.statusType, this.columnField)
      ] = this;
    }
    this.eGui = document.createElement("div");
    this.eGui.className = "ag-filter-body meeting-date-filter-panel";

    this.filterTypeSelect = document.createElement("select");
    this.filterTypeSelect.className =
      "ag-input-field-input meeting-date-floating-filter-select";

    DATE_FILTER_OPTIONS.forEach((option) => {
      const optionElement = document.createElement("option");
      optionElement.value = option;
      optionElement.textContent = DATE_FILTER_OPTION_LABELS[option] || option;
      this.filterTypeSelect.appendChild(optionElement);
    });

    this.dateFromInput = document.createElement("input");
    this.dateFromInput.type = "date";
    this.dateFromInput.className =
      "ag-input-field-input ag-text-field-input meeting-date-floating-filter-input";

    this.dateToInput = document.createElement("input");
    this.dateToInput.type = "date";
    this.dateToInput.className =
      "ag-input-field-input ag-text-field-input meeting-date-floating-filter-input";
    this.dateToInput.style.display = "none";

    this.filterTypeSelect.value = "equals";

    this.applyButton = document.createElement("button");
    this.applyButton.type = "button";
    this.applyButton.textContent = "Apply";
    this.applyButton.className = "meeting-date-filter-apply";
    this.bindApplyButton(this.applyButton);

    this.filterTypeSelect.addEventListener("change", () =>
      this.handleFilterTypeChange()
    );
    this.dateFromInput.addEventListener("change", () =>
      this.handleDateChanged()
    );
    this.dateToInput.addEventListener("change", () =>
      this.handleDateChanged()
    );
    this.filterTypeSelect.addEventListener("keydown", (event) =>
      this.handleKeyDown(event)
    );
    this.dateFromInput.addEventListener("keydown", (event) =>
      this.handleKeyDown(event)
    );
    this.dateToInput.addEventListener("keydown", (event) =>
      this.handleKeyDown(event)
    );

    this.eGui.appendChild(this.filterTypeSelect);
    this.eGui.appendChild(this.dateFromInput);
    this.eGui.appendChild(this.dateToInput);
    this.eGui.appendChild(this.applyButton);
  }

  bindApplyButton(button) {
    const handleApply = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      this.applyAndClose();
    };
    // onclick + capture: AG Grid often swallows bubble click before Apply runs.
    button.onclick = handleApply;
    button.addEventListener("pointerdown", handleApply, true);
    button.addEventListener("mousedown", handleApply, true);
    button.addEventListener("click", handleApply, true);
  }

  afterGuiAttached() {
    this._docApplyHandler = (event) => {
      if (!this.applyButton || !this.applyButton.contains(event.target)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      this.applyAndClose();
    };
    document.addEventListener("mousedown", this._docApplyHandler, true);
    document.addEventListener("pointerdown", this._docApplyHandler, true);
  }

  hideFilterPopup() {
    try {
      this.api?.hidePopupMenu?.();
    } catch (error) {
      // Popup may already be closed.
    }
  }

  applyAndClose() {
    if (this._applyInProgress) {
      return;
    }
    this._applyInProgress = true;
    this.publishModel();
    const statusType = resolveFilterStatus(this.statusType);
    // Fresh ref — not stale filterParams.onEnter from AG Grid init.
    if (meetingColumnFilterSearchRef.current) {
      triggerColumnFilterSearch(statusType);
    } else {
      this.onEnter?.(statusType);
    }
    this.hideFilterPopup();
    // Stay locked across pointerdown+mousedown+click duplicates.
  }

  handleFilterTypeChange() {
    const isRange = this.filterTypeSelect.value === "inRange";
    const isBlankType =
      this.filterTypeSelect.value === "blank" ||
      this.filterTypeSelect.value === "notBlank";

    this.dateFromInput.style.display = isBlankType ? "none" : "";
    this.dateToInput.style.display = isRange ? "" : "none";
    this.publishModel();
    if (
      this.filterTypeSelect.value === "blank" ||
      this.filterTypeSelect.value === "notBlank"
    ) {
      this.applyAndClose();
    }
  }

  handleDateChanged() {
    // Draft only — filters apply on Enter / Apply (same as text filters).
    this.publishModel();
  }

  buildModel() {
    const filterType = this.filterTypeSelect.value;

    if (filterType === "blank" || filterType === "notBlank") {
      return { filterType: "date", type: filterType };
    }

    if (filterType === "inRange") {
      if (!this.dateFromInput.value && !this.dateToInput.value) {
        return null;
      }

      const model = { filterType: "date", type: "inRange" };
      if (this.dateFromInput.value) {
        model.dateFrom = this.dateFromInput.value;
      }
      if (this.dateToInput.value) {
        model.dateTo = this.dateToInput.value;
      }
      return model;
    }

    if (!this.dateFromInput.value) {
      return null;
    }

    return {
      filterType: "date",
      type: filterType,
      dateFrom: this.dateFromInput.value,
    };
  }

  publishModel() {
    this.model = this.buildModel();
    this.syncDateDraftRef();
    this.filterChangedCallback();
  }

  syncDateDraftRef() {
    if (!this.statusType || !this.columnField) {
      return;
    }

    const drafts = getDateDraft(this.statusType);

    if (this.model) {
      drafts[this.columnField] = this.model;
      return;
    }

    delete drafts[this.columnField];
  }

  handleKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      this.applyAndClose();
    }
  }

  syncGuiFromModel(model) {
    if (!model) {
      this.filterTypeSelect.value = "equals";
      this.dateFromInput.value = "";
      this.dateToInput.value = "";
      this.dateFromInput.style.display = "";
      this.dateToInput.style.display = "none";
      return;
    }

    const filterType = model.type || "equals";
    this.filterTypeSelect.value = filterType;
    this.dateFromInput.value = model.dateFrom
      ? moment(model.dateFrom).format("YYYY-MM-DD")
      : "";
    this.dateToInput.value = model.dateTo
      ? moment(model.dateTo).format("YYYY-MM-DD")
      : "";
    this.dateFromInput.style.display =
      filterType === "blank" || filterType === "notBlank" ? "none" : "";
    this.dateToInput.style.display = filterType === "inRange" ? "" : "none";
  }

  getGui() {
    return this.eGui;
  }

  isFilterActive() {
    return !!this.model;
  }

  doesFilterPass() {
    return true;
  }

  getModel() {
    return this.model;
  }

  setModel(model) {
    this.model = model || null;
    this.syncGuiFromModel(this.model);
    // Only write drafts when a model is restored — never wipe drafts on
    // setModel(null) during AG Grid remounts (that raced the live search).
    if (model) {
      this.syncDateDraftRef();
    }
  }

  destroy() {
    if (this._docApplyHandler) {
      document.removeEventListener("mousedown", this._docApplyHandler, true);
      document.removeEventListener("pointerdown", this._docApplyHandler, true);
      this._docApplyHandler = null;
    }
    if (this.statusType && this.columnField) {
      delete meetingDateFilterInstancesRef.current[
        dateInstanceKey(this.statusType, this.columnField)
      ];
    }
  }
}

class BackendTextColumnFilter {
  init(params) {
    this.filterChangedCallback = params.filterChangedCallback;
    this.onEnter = params.filterParams?.onEnter;
    this.onLiveSearch = params.filterParams?.onLiveSearch;
    this.statusType = params.filterParams?.statusType;
    this.columnField = params.colDef?.field;
    this.searchField =
      params.filterParams?.searchField ||
      COLUMN_FIELD_TO_SEARCH_KEY[this.columnField] ||
      "";
    this.api = params.api;
    this.model = null;
    this.eGui = document.createElement("div");
    this.eGui.className = "ag-filter-body meeting-text-filter-panel";

    this.filterTypeSelect = document.createElement("select");
    this.filterTypeSelect.className =
      "ag-input-field-input meeting-date-floating-filter-select";

    TEXT_FILTER_OPTIONS.forEach((option) => {
      const optionElement = document.createElement("option");
      optionElement.value = option;
      optionElement.textContent = TEXT_FILTER_OPTION_LABELS[option] || option;
      this.filterTypeSelect.appendChild(optionElement);
    });
    this.filterTypeSelect.value = "contains";

    this.filterInput = document.createElement("input");
    this.filterInput.type = "text";
    this.filterInput.className = "ag-input-field-input ag-text-field-input";
    this.filterInput.placeholder = "Filter...";

    this.applyButton = document.createElement("button");
    this.applyButton.type = "button";
    this.applyButton.textContent = "Apply";
    this.applyButton.className = "meeting-text-filter-apply";
    this.bindApplyButton(this.applyButton);

    this.filterTypeSelect.addEventListener("change", () =>
      this.handleFilterTypeChange()
    );
    this.filterInput.addEventListener("input", () => this.handleInputChange());
    this.filterInput.addEventListener("keydown", (event) =>
      this.handleKeyDown(event)
    );

    this.eGui.appendChild(this.filterTypeSelect);
    this.eGui.appendChild(this.filterInput);
    this.eGui.appendChild(this.applyButton);
  }

  bindApplyButton(button) {
    const handleApply = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      this.applyAndClose();
    };
    // onclick + capture: AG Grid often swallows bubble click before Apply runs.
    button.onclick = handleApply;
    button.addEventListener("pointerdown", handleApply, true);
    button.addEventListener("mousedown", handleApply, true);
    button.addEventListener("click", handleApply, true);
  }

  afterGuiAttached() {
    this._docApplyHandler = (event) => {
      if (!this.applyButton || !this.applyButton.contains(event.target)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      this.applyAndClose();
    };
    document.addEventListener("mousedown", this._docApplyHandler, true);
    document.addEventListener("pointerdown", this._docApplyHandler, true);
  }

  hideFilterPopup() {
    try {
      this.api?.hidePopupMenu?.();
    } catch (error) {
      // Popup may already be closed.
    }
  }

  syncTextDraft() {
    if (!this.searchField) {
      return;
    }
    const value = this.filterInput?.value != null
      ? String(this.filterInput.value)
      : this.model?.filter != null
        ? String(this.model.filter)
        : "";
    setTextDraftValue(this.statusType, this.searchField, value.trim());
  }

  applyAndClose() {
    if (this._applyInProgress) {
      return;
    }
    this._applyInProgress = true;
    this.publishModel();
    // Force latest input into draft before fetch (survives AG Grid remount).
    if (this.searchField) {
      setTextDraftValue(
        this.statusType,
        this.searchField,
        this.filterInput.value.trim()
      );
    }
    const statusType = resolveFilterStatus(this.statusType);
    // Fresh ref — same path as Enter on floating filter (always current).
    if (meetingColumnFilterSearchRef.current) {
      triggerColumnFilterSearch(statusType);
    } else {
      this.onEnter?.(statusType);
    }
    this.hideFilterPopup();
    // Stay locked across pointerdown+mousedown+click duplicates.
  }

  handleFilterTypeChange() {
    const isBlankType =
      this.filterTypeSelect.value === "blank" ||
      this.filterTypeSelect.value === "notBlank";

    this.filterInput.style.display = isBlankType ? "none" : "";
    this.publishModel();
    // Filters apply on Enter/Apply only (blank/notBlank is an intentional apply).
    if (isBlankType) {
      this.applyAndClose();
    }
  }

  handleInputChange() {
    this.publishModel();
    const statusType = resolveFilterStatus(this.statusType);
    if (this.searchField) {
      setTextDraftValue(statusType, this.searchField, this.filterInput.value);
    }
    if (!this.filterInput.value.trim()) {
      // Erasing the value clears the filter immediately (no Apply/Enter).
      if (meetingColumnFilterSearchRef.current) {
        triggerColumnFilterSearch(statusType);
      } else {
        this.onEnter?.(statusType);
      }
      return;
    }
    // Live while typing (original admin behavior).
    this.onLiveSearch?.(statusType);
  }

  buildModel() {
    const filterType = this.filterTypeSelect.value;

    if (filterType === "blank" || filterType === "notBlank") {
      return { filterType: "text", type: filterType };
    }

    const filterValue = this.filterInput.value.trim();
    if (!filterValue) {
      return null;
    }

    return {
      filterType: "text",
      type: filterType,
      filter: filterValue,
    };
  }

  publishModel() {
    this.model = this.buildModel();
    this.syncTextDraft();
    this.filterChangedCallback();
  }

  handleKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      this.applyAndClose();
    }
  }

  syncGuiFromModel(model) {
    if (!model) {
      this.filterTypeSelect.value = "contains";
      this.filterInput.value = "";
      this.filterInput.style.display = "";
      return;
    }

    const filterType = model.type || "contains";
    this.filterTypeSelect.value = filterType;
    this.filterInput.value =
      model.filter != null ? String(model.filter) : "";
    this.filterInput.style.display =
      filterType === "blank" || filterType === "notBlank" ? "none" : "";
  }

  getGui() {
    return this.eGui;
  }

  isFilterActive() {
    return !!this.model;
  }

  doesFilterPass() {
    return true;
  }

  getModel() {
    return this.model;
  }

  setModel(model) {
    this.model = model || null;
    this.syncGuiFromModel(this.model);
    if (model) {
      this.syncTextDraft();
    }
  }

  destroy() {
    if (this._docApplyHandler) {
      document.removeEventListener("mousedown", this._docApplyHandler, true);
      document.removeEventListener("pointerdown", this._docApplyHandler, true);
      this._docApplyHandler = null;
    }
  }
}

class MeetingSearchFloatingFilter extends React.Component {
  getSearchField = () => {
    if (this.props.searchField) {
      return this.props.searchField;
    }

    const columnField = this.props.column?.getColDef?.()?.field;
    return COLUMN_FIELD_TO_SEARCH_KEY[columnField] || "";
  };

  getStoredValue = () => {
    const searchField = this.getSearchField();
    if (!searchField) {
      return "";
    }
    const statusType = resolveFilterStatus(this.props.statusType);
    return getTextDraft(statusType)[searchField] || "";
  };

  constructor(props) {
    super(props);
    this.state = {
      value:
        props.statusType && props.searchField
          ? getTextDraft(props.statusType)[props.searchField] || ""
          : "",
    };
  }

  componentDidMount() {
    const storedValue = this.getStoredValue();
    if (storedValue && storedValue !== this.state.value) {
      this.setState({ value: storedValue });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetNonce !== this.props.resetNonce) {
      const storedValue = this.getStoredValue();
      this.setState({ value: storedValue });
    }
  }

  onParentModelChanged(parentModel) {
    const value =
      parentModel?.filter != null ? String(parentModel.filter) : "";
    const searchField = this.getSearchField();

    if (searchField) {
      setTextDraftValue(this.props.statusType, searchField, value);
    }

    if (value !== this.state.value) {
      this.setState({ value });
    }
  }

  syncValue = (rawValue) => {
    const searchField = this.getSearchField();
    const statusType = resolveFilterStatus(this.props.statusType);
    if (searchField) {
      setTextDraftValue(statusType, searchField, rawValue);
    }

    if (this.props.onFloatingFilterChanged) {
      this.props.onFloatingFilterChanged(
        rawValue
          ? {
            filter: rawValue,
            filterType: "text",
            type: "contains",
          }
          : null
      );
    }

    return rawValue;
  };

  triggerColumnFilter = (rawValue) => {
    const searchField = this.getSearchField();
    const statusType = setTextDraftValue(
      this.props.statusType,
      searchField,
      rawValue
    );
    if (!String(rawValue || "").trim()) {
      // Erasing the value clears the filter immediately (no Apply/Enter).
      if (meetingColumnFilterSearchRef.current) {
        triggerColumnFilterSearch(statusType);
      } else {
        this.props.onEnter?.(statusType);
      }
      return;
    }
    if (this.props.onLiveSearch) {
      this.props.onLiveSearch(statusType);
      return;
    }
    this.props.onEnter?.(statusType);
  };

  handleInputChange = (event) => {
    const rawValue = event.target.value;
    this.syncValue(rawValue);
    this.setState({ value: rawValue });
    this.triggerColumnFilter(rawValue);
  };

  handleBlur = (event) => {
    this.syncValue(event.target.value);
  };

  handleCompositionEnd = (event) => {
    const rawValue = event.target.value;
    this.syncValue(rawValue);
    this.setState({ value: rawValue });
    this.triggerColumnFilter(rawValue);
  };

  handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      const rawValue = event.currentTarget.value;
      this.syncValue(rawValue);
      const statusType = setTextDraftValue(
        this.props.statusType,
        this.getSearchField(),
        rawValue
      );
      this.props.onEnter?.(statusType);
    }
  };

  render() {
    return (
      <input
        type={this.props.inputType || "text"}
        value={this.state.value}
        placeholder={this.props.placeholder || "חיפוש..."}
        onChange={this.handleInputChange}
        onBlur={this.handleBlur}
        onCompositionEnd={this.handleCompositionEnd}
        onKeyDown={this.handleKeyDown}
        className="ag-input-field-input ag-text-field-input"
        style={{ width: "100%" }}
      />
    );
  }
}

const formatDateFilterDisplay = (parentModel) => {
  if (!parentModel) {
    return "";
  }

  const filterType = parentModel.type || "equals";

  if (filterType === "blank" || filterType === "notBlank") {
    return DATE_FILTER_OPTION_LABELS[filterType] || filterType;
  }

  if (filterType === "inRange") {
    const from = parentModel.dateFrom
      ? formatFilterDate(parentModel.dateFrom)
      : "";
    const to = parentModel.dateTo ? formatFilterDate(parentModel.dateTo) : "";
    if (from && to) {
      return `${from} - ${to}`;
    }
    return from || to;
  }

  if (parentModel.dateFrom) {
    const formattedDate = formatFilterDate(parentModel.dateFrom);
    if (filterType === "equals") {
      return formattedDate;
    }
    const label = DATE_FILTER_OPTION_LABELS[filterType] || filterType;
    return `${label}: ${formattedDate}`;
  }

  return "";
};

class MeetingDateFilterTriggerFloatingFilter extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      displayText: "",
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetNonce !== this.props.resetNonce) {
      this.setState({ displayText: "" });
    }
  }

  onParentModelChanged(parentModel) {
    this.setState({ displayText: formatDateFilterDisplay(parentModel) });
  }

  handleOpenFilter = () => {
    this.props.showParentFilter?.();
  };

  handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      this.props.onEnter?.();
      return;
    }

    if (event.key === " ") {
      event.preventDefault();
      this.handleOpenFilter();
    }
  };

  render() {
    return (
      <input
        type="text"
        readOnly
        value={this.state.displayText}
        placeholder="חיפוש..."
        onClick={this.handleOpenFilter}
        onFocus={this.handleOpenFilter}
        onKeyDown={this.handleKeyDown}
        className="ag-input-field-input ag-text-field-input meeting-date-filter-trigger"
        style={{ width: "100%", cursor: "pointer" }}
        aria-label="Open date filter"
      />
    );
  }
}

let gridApi = {};
const MeetingDetails = () => {
  const { t } = useTranslation();
  const lang = localStorage.getItem("lang");
  const gridRef = useRef([]);
  const [id, setId] = useState();
  const [clientId, setClientId] = useState();
  const [document, setDocument] = useState([]);
  const [documentModal, setDocumentModal] = useState(false);
  const [offerModal, setOfferModal] = useState(false);
  const [offerMeetingId, setoffermeetingId] = useState();
  const [newOfferDate, setDates] = useState([]);
  const [consultId, setConsultID] = useState();
  const [mstatus, setMStatus] = useState();
  const [doctorId, setDoctorId] = useState();
  const [assapValue, setAsapValue] = useState();
  const anchorRef = useRef(null);
  const [docID, setDocID] = useState("")

  const handleCloseModal = () => (
    setOfferModal(false),
    refreshMeetings(),
    setSDate(),
    setFDate(),
    setFTime(),
    setSTime(),
    setStartDate(),
    setStartDate1(),
    setDates([]),
    setConsultID()
  );

  const [fdate, setFDate] = useState();
  const [sdate, setSDate] = useState();
  const [ftime, setFTime] = useState();
  const [stime, setSTime] = useState();
  const [startDate, setStartDate] = useState(new Date());
  const [startDate1, setStartDate1] = useState(new Date());
  const [statusData, setStatusData] = useState(createStatusDataMap);
  const [counts, setCounts] = useState({ statusCounts: {}, totalCount: 0 });
  const status = useMemo(
    () =>
      MEETING_STATUS_COLUMNS.map((type) => ({
        type,
        meetings: statusData[type].rows,
        total: statusData[type].total,
        page: statusData[type].page,
        totalPages: statusData[type].totalPages,
        loading: statusData[type].loading,
      })),
    [statusData]
  );

  const bulkActionMenu = [
    { type: "Change status to", id: "moveto", },
    { type: "Update Doctor", id: "updateDoc" },
  ]

  const [comentmeetingid, setCommentMeetingId] = useState();
  const [comment, setComment] = useState();
  const [commentModal, setCommentModal] = useState(false);
  const handleCloseComment = () => (setCommentModal(false), refreshMeetings());
  const [consultData, setConsultData] = useState([]);
  const [consultfilter, setConsultfilter] = useState([]);
  const [updateId, setUpdateId] = useState();
  const [updateData, setUpdateData] = useState([]);
  const [pacseeDocument, setPacseeDocument] = useState([]);
  const [updateModal, setUpdateModal] = useState(false);
  const handleClose = () => (setUpdateModal(false), refreshMeetings());
  const [priceModal, setPriceModal] = useState(false);
  const priceClose = () => (setPriceModal(false), refreshMeetings());
  const [offerDates, setOfferDates] = useState([]);
  const [offerData, setofferdata] = useState([]);
  const [reqdates, setReqDates] = useState([]);
  const [reportModal, setReportModal] = useState(false);
  const [meetinglogId, setMeetinglogId] = useState();
  const [meetinglogmodal, setMeetinglogmodal] = useState(false);
  const handleMeetingLogModal = () => setMeetinglogmodal(false);
  const handleReportModalClose = () => setReportModal(false);
  const [detailsopen, setDetailsOpen] = useState(false);
  const [role, setRole] = useState();
  const [userid, setUserID] = useState();
  const [meetingKey, setMeetingKey] = useState(["Scheduled", "DatesOffered", "Canceled", "Done", "NoShow", "AdminCare", "Created"]);
  const [checked, setChecked] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [loader, setLoader] = useState(false);
  const [countsLoading, setCountsLoading] = useState(true);
  const [idfModal, setIdfModal] = useState(false);
  const [myDoctorModal, setMyDoctorModal] = useState(false);
  const [showAction, setShowAction] = useState(false);
  const [selectedMeetingIds, setSelectedMeetingIds] = useState({});
  const [currentStatus, setCurrentStatus] = useState("");
  const handleDetailsClose = () => (setDetailsOpen(false), setUserID());
  const [doctorList, setDoctorList] = useState([]);
  const [meetingType, setMeetingType] = useState("");
  const appliedSearchRef = useRef(
    createPerStatusMap(createEmptyAppliedSearch)
  );
  const fetchRequestIdRef = useRef(0);
  const statusFetchIdRef = useRef(createPerStatusMap(() => 0));
  const requestedRef = useRef(new Set());
  const activeStatusRef = useRef("Scheduled");
  const setFocusedStatus = useCallback((statusType) => {
    if (!MEETING_STATUS_COLUMNS.includes(statusType)) {
      return;
    }
    activeStatusRef.current = statusType;
    focusedMeetingStatusRef.current = statusType;
  }, []);
  const loadStatusPageRef = useRef(null);
  const globalSearchRef = useRef(null);
  const globalCustomerNameRef = useRef("");
  const debounceTimersRef = useRef({});
  const [searchResetNonce, setSearchResetNonce] = useState(0);
  const [searchNonce, setSearchNonce] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);

  const clearDebounceTimers = useCallback(() => {
    Object.values(debounceTimersRef.current).forEach((timerId) => {
      if (timerId) {
        clearTimeout(timerId);
      }
    });
    debounceTimersRef.current = {};
  }, []);

  const markSearchActive = useCallback(() => {
    setIsSearchMode(
      MEETING_STATUS_COLUMNS.some((statusType) => {
        const applied = appliedSearchRef.current[statusType];
        return hasActiveSearch(applied?.searchFields, applied?.search);
      }) || Boolean(globalCustomerNameRef.current)
    );
  }, []);

  const applyStatusSearch = useCallback(
    (statusType) => {
      const resolvedStatus = resolveFilterStatus(
        statusType || activeStatusRef.current
      );

      setFocusedStatus(resolvedStatus);
      flushDateFilterModels(resolvedStatus);

      const searchFields = buildSearchFieldsFromFilterModel(
        getMergedFilterModel(resolvedStatus),
        resolvedStatus
      );
      // Keep global customer search independent from column filters.
      const search = globalCustomerNameRef.current;

      // Column filters stay scoped to this status list only.
      appliedSearchRef.current[resolvedStatus] = {
        searchFields: { ...searchFields },
        search,
      };
      markSearchActive();
      loadStatusPageRef.current?.(resolvedStatus, {
        page: 1,
        append: false,
      });
    },
    [markSearchActive, setFocusedStatus]
  );

  const applyGlobalCustomerSearch = useCallback(() => {
    const customerName = globalSearchRef.current?.value?.trim() || "";
    globalCustomerNameRef.current = customerName;

    // Global Search only updates customer name — never clears column filters.
    MEETING_STATUS_COLUMNS.forEach((statusType) => {
      const previous =
        appliedSearchRef.current[statusType] || createEmptyAppliedSearch();
      appliedSearchRef.current[statusType] = {
        searchFields: { ...(previous.searchFields || {}) },
        search: customerName,
      };
    });

    markSearchActive();
    setSearchNonce((n) => n + 1);
  }, [markSearchActive]);

  const runSearchNow = useCallback(
    (statusType) => {
      const isColumnFilter = MEETING_STATUS_COLUMNS.includes(statusType);
      const timerKey = isColumnFilter ? statusType : "__global__";
      if (debounceTimersRef.current[timerKey]) {
        clearTimeout(debounceTimersRef.current[timerKey]);
        debounceTimersRef.current[timerKey] = null;
      }

      if (isColumnFilter) {
        applyStatusSearch(statusType);
        return;
      }
      applyGlobalCustomerSearch();
    },
    [applyStatusSearch, applyGlobalCustomerSearch]
  );

  // Keep AG Grid popup Apply on the latest search (filterParams go stale on init).
  meetingColumnFilterSearchRef.current = runSearchNow;

  const scheduleLiveSearch = useCallback(
    (statusType) => {
      const isColumnFilter = MEETING_STATUS_COLUMNS.includes(statusType);
      const timerKey = isColumnFilter
        ? statusType
        : "__global__";

      if (debounceTimersRef.current[timerKey]) {
        clearTimeout(debounceTimersRef.current[timerKey]);
      }

      if (!isColumnFilter) {
        const rawValue = globalSearchRef.current?.value ?? "";
        if (!String(rawValue).trim()) {
          debounceTimersRef.current[timerKey] = null;
          applyGlobalCustomerSearch();
          return;
        }
      }

      debounceTimersRef.current[timerKey] = setTimeout(() => {
        debounceTimersRef.current[timerKey] = null;
        runSearchNow(statusType);
      }, SEARCH_DEBOUNCE_MS);
    },
    [applyGlobalCustomerSearch, runSearchNow]
  );

  const handleClearSearch = useCallback(() => {
    clearDebounceTimers();
    meetingDraftSearchRef.current = createPerStatusMap(createEmptySearchFields);
    meetingDateDraftRef.current = createPerStatusMap(() => ({}));
    appliedSearchRef.current = createPerStatusMap(createEmptyAppliedSearch);
    globalCustomerNameRef.current = "";
    if (globalSearchRef.current) {
      globalSearchRef.current.value = "";
    }
    clearAllGridFilters();
    setSearchResetNonce((n) => n + 1);
    setIsSearchMode(false);
    setSearchNonce((n) => n + 1);
  }, [clearDebounceTimers]);

  useEffect(() => {
    return () => {
      clearDebounceTimers();
    };
  }, [clearDebounceTimers]);

  const clearAllSelections = () => {
    MEETING_STATUS_COLUMNS.forEach((statusType) => {
      if (gridApi[statusType]) {
        gridApi[statusType].deselectAll();
      }
    });

    // Reset all selection-related states
    setSelectedMeetingIds({});
    setCurrentStatus("");
    setShowAction(false);
    setDocID("");
    setDoctorList([]);
  };

  useEffect(() => {
    if (docID && docID != "") {
      getDoctorList(docID)
    }
    else {
      setDoctorList([])
    }
  }, [docID])


  const handleUpdateDoctor = async (targetValue, id) => {
    const payload = { meetingIds: id, "DoctorId": targetValue }
    try {
      const response = await adminUpdateAppointmentById(payload);
      if ([200, 201, 202].includes(response.status)) {
        toast.success(response.data.msg);
        clearAllSelections(); // Clear all selections and reset state
        await refreshMeetings();
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Get Doc List
  const getDoctorList = async (id) => {
    try {
      const response = await getDoctorsByConsultationId(id);
      let docDetails = response.data.doctors.map(doc => {
        return {
          name: `${doc?.doctorData?.first_name} ${doc?.doctorData?.last_name}`,
          doctorId: doc?.doctorData._id
        }
      })

      setDoctorList(docDetails);
    } catch (error) {
      console.log(error);
    }
  };

  const getParams = (statusType) => ({
    allColumns: true,
    fileName: `${statusType}-${moment(new Date()).format("DD-MM-YYYY hh:mm A")}`,
    columnKeys: [
      "Meeting Id",
      "Schedule Date",
      "Created Date",
      "Client Name",
      "Physician Name",
      "consultingField",
      "MeetingStatus",
      "Admin comments",
      "Offer Date",
      "Request Description",
      "Age",
      "Email",
      "Client Id",
      "DOB",
      "Gender",
      "Phone",
      "Marital Status",
      "Address",
    ],
  });

  const getGridOptions = (statusType) => ({
    isRowSelectable: (node) => {
      return node.data.currentStatus === statusType || !node.data.currentStatus;
    },
  });

  const handleIdfUpload = () => {
    setIdfModal(!idfModal);
  };
  const handleMyDoctorUpload = () => {
    setMyDoctorModal(!myDoctorModal);
  };

  const getBackendSearchFilterColDef = (searchField, statusType) => ({
    filter: BackendTextColumnFilter,
    filterParams: {
      onEnter: (resolvedStatus) =>
        runSearchNow(resolvedStatus || statusType),
      onLiveSearch: (resolvedStatus) =>
        scheduleLiveSearch(resolvedStatus || statusType),
      statusType,
      searchField,
    },
    floatingFilter: true,
    floatingFilterComponent: MeetingSearchFloatingFilter,
    floatingFilterComponentParams: {
      searchField,
      statusType,
      onEnter: (resolvedStatus) =>
        runSearchNow(resolvedStatus || statusType),
      onLiveSearch: (resolvedStatus) =>
        scheduleLiveSearch(resolvedStatus || statusType),
      resetNonce: searchResetNonce,
    },
  });

  const getDateFilterColDef = (statusType) => ({
    filter: BackendDateColumnFilter,
    filterParams: {
      onEnter: () => runSearchNow(statusType),
      statusType,
    },
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    floatingFilterComponent: MeetingDateFilterTriggerFloatingFilter,
    floatingFilterComponentParams: {
      onEnter: () => runSearchNow(statusType),
      resetNonce: searchResetNonce,
    },
  });

  const getColumnDefs = (currentStatus, statusType) => [
    {
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true, // Add this line
      checkboxSelection: (params) =>
        params.data.currentStatus === statusType || !params.data.currentStatus,
      field: "Meeting Id",
      ...getBackendSearchFilterColDef("meetingId", statusType),
      headerClass: !(currentStatus === statusType || !currentStatus)
        ? "disabled-checkbox-cell"
        : "",
      cellClassRules: {
        "disabled-checkbox-cell": () =>
          !(currentStatus === statusType || !currentStatus),
      },
      minWidth: window.innerWidth < 991 ? 230 : 110,
    },
    {
      field: "Schedule Date",
      ...getDateFilterColDef(statusType),
      minWidth: window.innerWidth < 991 ? 280 : 240,
      valueFormatter: (params) => {
        return params.value
          ? moment(params.value).format("DD-MM-YYYY h:mm:ss a")
          : "";
      },
    },
    {
      field: "Created Date",
      ...getDateFilterColDef(statusType),
      minWidth: window.innerWidth < 991 ? 280 : 240,
      refreshValuesOnOpen: true,
      sort: "desc",
      valueFormatter: (params) => {
        return params.value
          ? moment(params.value).format("DD-MM-YYYY h:mm:ss a")
          : "";
      },
    },
    {
      field: "Client Name",
      ...getBackendSearchFilterColDef("clientName", statusType),
      onCellClicked(params) {
        setDetailsOpen(true);
        setRole("client");
        setUserID(params.data?.data?.clientData._id);
      },
    },

    {
      field: "Physician Name",
      ...getBackendSearchFilterColDef("physicianName", statusType),
      onCellClicked(params) {
        setDetailsOpen(
          params.data?.data?.doctorData?._id ||
            params.data?.data?.offerDates[0].DoctorId
            ? true
            : false
        );
        setRole("doctor");
        setUserID(
          params.data?.data?.doctorData
            ? params.data?.data?.doctorData._id
            : params.data?.data?.offerDates[0]
              ? params.data?.data?.offerDates[0]?.DoctorId
              : null
        );
      },
    },
    {
      field: "consultingField",
      ...getBackendSearchFilterColDef("consultingField", statusType),
      minWidth: window.innerWidth < 991 ? 150 : 110,
    },
    {
      field: "MeetingStatus",
      ...getBackendSearchFilterColDef("meetingStatus", statusType),
      minWidth: window.innerWidth < 991 ? 150 : 110,
      cellRendererFramework: (params) => (
        <div>
          <select
            className="form-control"
            name="statuses"
            onChange={(e) => handleChangestatus(e, params.data.data._id)}
            id="statuses"
            defaultValue={params.data.MeetingStatus}
          >
            {status.map((d, i) => {
              return (
                <option key={i} value={d.type}>
                  {d.type}
                </option>
              );
            })}
          </select>
        </div>
      ),
    },
    {
      field: "Admin comments",
      ...getBackendSearchFilterColDef("comments", statusType),
      valueGetter: (params) => {
        return params.data?.Comments;
      },
      cellRendererFramework: (params) => (
        <div
          onClick={() =>
            viewComment(params.data.data._id, params.data.Comments)
          }
          className="commentColumn"
        >
          {params.data?.Comments
            ? `${params.data?.Comments.slice(0, 10)}${params.data?.Comments.length > 10 ? "......" : ""
            }`
            : "Add Comment"}
        </div>
      ),
    },
    {
      headerName: "View Document",
      width: window.innerWidth < 991 ? 120 : "unset",
      filter: false,
      floatingFilter: false,
      cellRendererFramework: (params) => (
        <div>
          <Button
            onClick={() =>
              viewDocument(
                params.data.data._id,
                params.data.data.clientData._id,
                params.data.data
              )
            }
          >
            View
          </Button>
        </div>
      ),
    },
    {
      headerName: "Action",
      width: 100,
      filter: false,
      floatingFilter: false,
      cellRendererFramework: (params) => (
        <div>
          <Button
            onClick={() =>
              view(
                params.data.data._id,
                params.data.data.consultation._id,
                params.data.data,
                params.data.data?.offerDates
              )
            }
          >
            Update
          </Button>
        </div>
      ),
    },
    {
      field: "Offer Date",
      headerName: "Offer Date",
      width: 100,
      filter: false,
      floatingFilter: false,
      cellRendererFramework: (params) => (
        <div>
          {params.data.data?.requestedDates != 0 && !params.data.data.ASAP ? (
            <Button
              disabled={
                params.data.MeetingStatus == "Created" ||
                  params.data.MeetingStatus == "DatesOffered"
                  ? false
                  : true
              }
              onClick={() =>
                openModal(
                  params.data.data._id,
                  params.data.data?.requestedDates,
                  params.data.data?.consultation._id,
                  params.data.data?.offerDates,
                  params.data.MeetingStatus,
                  params.data?.data?.requestedDates,
                  params.data?.data?.ASAP
                )
              }
            >
              Offer
            </Button>
          ) : (
            <Button
              disabled={
                params.data.MeetingStatus == "Created" ||
                  params.data.MeetingStatus == "DatesOffered"
                  ? false
                  : true
              }
              onClick={() =>
                asssonpossible(
                  params.data.data._id,
                  params.data.data?.consultation._id,
                  params.data.data?.offerDates,
                  params.data.MeetingStatus,
                  params.data?.data?.requestedDates,
                  params.data?.data?.ASAP
                )
              }
            >
              Offer
            </Button>
          )}
        </div>
      ),
    },
    {
      field: "Notification",
      filter: false,
      editable: false,
      sortable: false,
      minWidth: 110,
      maxWidth: 120,
      width: 110,
      cellRendererFramework: (params) => (
        <div>
          <input
            type="checkbox"
            className="noification_check"
            defaultChecked={params.data?.data?.isNotification ? true : false}
            onClick={(e) =>
              UpdateDetails(e.target.checked, params.data.data._id)
            }
          />
        </div>
      ),
    },
    {
      field: "Meeting Logs",
      filter: false,
      editable: false,
      sortable: false,
      cellRendererFramework: (params) => (
        <div>
          <Button onClick={() => meetinglog(params.data.data._id)}>View</Button>
        </div>
      ),
    },
    { field: "Request Description", hide: true },
    { field: "Age", hide: true },
    { field: "Email", hide: true },
    { field: "Client Id", hide: true },
    {
      field: "DOB",
      hide: true,
      valueFormatter: (params) => {
        return params.value ? moment(params.value).format("DD-MM-YYYY") : "";
      },
    },
    { field: "Gender", hide: true },
    { field: "Phone", hide: true },
    { field: "Marital Status", hide: true },
    { field: "Address", hide: true },
  ];
  const view = (id, id2, data, offerdata) => {
    const c = consultData.filter((d) => {
      return d._id == id2;
    });
    setConsultfilter(c);
    setUpdateId(id);
    setUpdateData(data);
    setofferdata(offerdata);
    setDoctorId(
      data.length != 0 && data?.DoctorId
        ? data?.DoctorId
        : data.offerDates &&
          data.offerDates.length != 0 &&
          data?.statuses !== "Scheduled"
          ? data.offerDates[0].DoctorId
          : ""
    );
    setUpdateModal(true);
  };
  const openModal = (
    id,
    dates,
    cid,
    offerdate,
    meetingstatus,
    requesteddates,
    asapvalue
  ) => {
    setoffermeetingId(id);
    setDates(dates);
    setOfferDates(offerdate);
    setConsultID(cid);
    setMStatus(meetingstatus);
    setReqDates(requesteddates);
    setAsapValue(asapvalue);
    setOfferModal(true);
  };
  const asssonpossible = (
    id,
    cid,
    offerdate,
    meetingstatus,
    requesteddates,
    asapvalue
  ) => {
    setoffermeetingId(id);
    setOfferDates(offerdate);
    setConsultID(cid);
    setMStatus(meetingstatus);
    setReqDates(requesteddates);
    setOfferModal(true);
    setAsapValue(asapvalue);
  };

  const viewComment = (id, data) => {
    setCommentMeetingId(id);
    setComment(data);
    setCommentModal(true);
  };

  const consultingFields = async () => {
    try {
      const response = await adminAllConsultation();
      setConsultData(response.data.result);
    } catch (error) {
      console.log(error);
    }
  };
  const allDocument = async (id) => {
    try {
      const response = await getAllDocuments(id);

      if ([200, 201, 202].includes(response.data.code)) {
        setDocument(response.data.meetingDocuments);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const viewDocument = async (id, id2, data) => {
    setMeetingType(data?.meetingType);
    setId(id);
    setClientId(id2);
    setDocumentModal(true);
    allDocument(id);
    mridocument(id);
  };
  const mridocument = async (id) => {
    try {
      const response = await getMriDocuments(id);
      setPacseeDocument(response.data.pacseeFiles);
    } catch (error) {
      console.log(error);
    }
  };
  const handleChangestatus = (event, id) => {
    let targetValue = event.target.value;
    statusUpload(targetValue, id);
  };
  const statusUpload = async (targetValue, id) => {
    const data = {
      statuses: targetValue,
      meetingIds: [id],
    };
    try {
      const response = await adminUpdateAppointmentById(data);
      if ([200, 201, 202].includes(response.status)) {
        toast.success(response.data.msg);
        refreshMeetings();
      }
    } catch (error) {
      console.log(error);
    }
  };
  const meetinglog = (id) => {
    setMeetinglogId(id);
    setMeetinglogmodal(true);
  };
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: false,
    editable: true,
    resizable: true,
    floatingFilter: false,
    flex: window.innerWidth < 991 ? 0 : 1,
    minWidth: 110,
  }));

  const cellClickedListener = useCallback((event) => {
    const statusType = Object.keys(gridApi).find(
      (key) => gridApi[key] === event.api
    );
    if (MEETING_STATUS_COLUMNS.includes(statusType)) {
      setFocusedStatus(statusType);
    }
  }, [setFocusedStatus]);

  useEffect(() => {
    consultingFields();
  }, []);

  // Global Search → clientName. Column filters → searchFields. Independent layers.
  const getPayloadSearchFields = useCallback((statusType) => {
    const applied =
      appliedSearchRef.current[statusType] || createEmptyAppliedSearch();
    const fields = { ...(applied.searchFields || {}) };
    const customerName = String(
      applied.search || globalCustomerNameRef.current || ""
    ).trim();
    // Search box owns customer name; do not drop column filters like consultingField.
    if (customerName) {
      fields.clientName = customerName;
    }
    return buildSearchFieldsPayload(fields);
  }, []);

  const fetchAllRowsForStatus = useCallback(
    async (statusType) => {
      const allRows = [];
      let page = 1;
      let totalPages = 1;

      while (page <= totalPages) {
        const response = await adminAppointmentsPage({
          status: statusType,
          page,
          perPage: DEFAULT_PER_PAGE,
          searchFields: getPayloadSearchFields(statusType),
        });
        allRows.push(...formatStatusRows(response, lang));
        totalPages = response?.data?.totalPages ?? 1;
        page += 1;
      }

      return allRows;
    },
    [getPayloadSearchFields, lang]
  );

  const loadCounts = useCallback(
    async (requestId) => {
      setCountsLoading(true);
      try {
        const response = await adminAppointmentCounts({
          searchFields: globalCustomerNameRef.current
            ? { clientName: globalCustomerNameRef.current }
            : {},
        });

        if (requestId !== fetchRequestIdRef.current) {
          return;
        }

        const data = response?.data ?? {};
        setCounts({
          statusCounts: data.statusCounts ?? {},
          totalCount: data.totalCount ?? 0,
        });
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          return;
        }
        console.log(error);
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setCountsLoading(false);
        }
      }
    },
    []
  );

  const loadStatusPage = useCallback(
    async (type, { page, append = false, background = false } = {}) => {
      const globalRequestId = fetchRequestIdRef.current;
      if (statusFetchIdRef.current[type] == null) {
        statusFetchIdRef.current[type] = 0;
      }
      const requestId = ++statusFetchIdRef.current[type];

      setStatusData((prev) => ({
        ...prev,
        [type]: { ...prev[type], loading: true },
      }));

      try {
        const response = await adminAppointmentsPage({
          status: type,
          page,
          perPage: DEFAULT_PER_PAGE,
          searchFields: getPayloadSearchFields(type),
        });

        if (
          requestId !== statusFetchIdRef.current[type] ||
          globalRequestId !== fetchRequestIdRef.current
        ) {
          return;
        }

        const data = response?.data ?? {};
        const newRows = formatStatusRows(response, lang);
        const resolvedPage = data.page ?? page;
        const resolvedTotalPages = data.totalPages ?? 0;

        setStatusData((prev) => {
          const current = prev[type];
          const rows = append ? [...current.rows, ...newRows] : newRows;
          return {
            ...prev,
            [type]: {
              rows,
              page: resolvedPage,
              total: data.total ?? rows.length,
              totalPages: resolvedTotalPages,
              loading: false,
              loaded: true,
            },
          };
        });

        // Lazy background top-up: if the first pull is nearly empty but more
        // rows exist, fetch the next page in the background without blocking.
        if (
          !background &&
          !append &&
          newRows.length < LAZY_MIN_ROWS &&
          resolvedPage < resolvedTotalPages
        ) {
          loadStatusPage(type, {
            page: resolvedPage + 1,
            append: true,
            background: true,
          });
        }
      } catch (error) {
        if (
          requestId !== statusFetchIdRef.current[type] ||
          globalRequestId !== fetchRequestIdRef.current
        ) {
          return;
        }
        console.log(error);
        setStatusData((prev) => ({
          ...prev,
          [type]: { ...prev[type], loading: false, loaded: true },
        }));
        toast.error(t("Failed to load appointments"));
      }
    },
    [getPayloadSearchFields, lang, t]
  );

  loadStatusPageRef.current = loadStatusPage;

  const handleLoadMore = useCallback(
    (type) => {
      const current = statusData[type];
      if (!current || current.loading || current.page >= current.totalPages) {
        return;
      }
      loadStatusPage(type, { page: current.page + 1, append: true });
    },
    [statusData, loadStatusPage]
  );

  const handleLoadAll = useCallback(
    async (type) => {
      const current = statusData[type];
      if (!current || current.loading || current.page >= current.totalPages) {
        return;
      }

      const globalRequestId = fetchRequestIdRef.current;
      if (statusFetchIdRef.current[type] == null) {
        statusFetchIdRef.current[type] = 0;
      }
      const requestId = ++statusFetchIdRef.current[type];

      setStatusData((prev) => ({
        ...prev,
        [type]: { ...prev[type], loading: true },
      }));

      try {
        const allRows = await fetchAllRowsForStatus(type);
        if (
          requestId !== statusFetchIdRef.current[type] ||
          globalRequestId !== fetchRequestIdRef.current
        ) {
          return;
        }

        const totalPages = Math.max(current.totalPages || 1, 1);
        setStatusData((prev) => ({
          ...prev,
          [type]: {
            rows: allRows,
            page: totalPages,
            total: current.total || allRows.length,
            totalPages,
            loading: false,
            loaded: true,
          },
        }));
      } catch (error) {
        if (
          requestId !== statusFetchIdRef.current[type] ||
          globalRequestId !== fetchRequestIdRef.current
        ) {
          return;
        }
        console.log(error);
        setStatusData((prev) => ({
          ...prev,
          [type]: { ...prev[type], loading: false, loaded: true },
        }));
        toast.error(t("Failed to load appointments"));
      }
    },
    [statusData, fetchAllRowsForStatus, t]
  );

  const refreshMeetings = useCallback(() => {
    setSearchNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const requestId = ++fetchRequestIdRef.current;
    requestedRef.current = new Set();
    statusFetchIdRef.current = createPerStatusMap(() => 0);
    setStatusData(createStatusDataMap());
    clearAllSelections();
    loadCounts(requestId);
  }, [searchNonce]);

  useEffect(() => {
    meetingKey.forEach((type) => {
      if (!MEETING_STATUS_COLUMNS.includes(type)) {
        return;
      }
      const requestKey = `${searchNonce}:${type}`;
      if (requestedRef.current.has(requestKey)) {
        return;
      }
      requestedRef.current.add(requestKey);
      loadStatusPage(type, { page: 1, append: false });
    });
  }, [searchNonce, meetingKey, loadStatusPage]);
  const rowClassRules = useMemo(() => {
    return {
      "admin-prescription-only": (params) => {
        const status = params.data.data.statuses;
        const type = params.data.data.meetingType;
        return status === "Done" && type === "PrescriptionOnly";
      },
    };
  }, []);
  const UpdateDetails = async (checkedValue, meetingid) => {
    const data = {
      isNotification: checkedValue,
      meetingIds: [meetingid],
    };
    try {
      const response = await adminUpdateAppointmentById(data);
      if ([200, 201, 202].includes(response.status)) {
        toast.success(t(response.data.msg));
        handleClose();
      }
    } catch (error) {
      console.log(error);
    }
  };
  const onGridReady = useCallback((params, i) => {
    gridApi[i] = params.api;
    if (window.innerWidth < 991) {
      params.api.sizeColumnsToFit();
    }
  }, []);
  const onExportClick = useCallback(async () => {
    if (!checked.length) {
      toast.warning(t("Select at least one status to export"));
      return;
    }

    const prevStatusData = status;
    setExporting(true);

    try {
      for (const statusType of checked) {
        const exportRows = await fetchAllRowsForStatus(statusType);
        const gridApiInstance = gridApi[statusType];

        if (!gridApiInstance) {
          const response = await adminAppointmentsExport({
            searchFields: getPayloadSearchFields(statusType),
            status: statusType,
          });
          const url = window.URL.createObjectURL(response.data);
          const filename = `${statusType}-${moment(new Date()).format(
            "DD-MM-YYYY hh:mm A"
          )}.csv`;
          anchorRef.current.href = url;
          anchorRef.current.setAttribute("download", filename);
          anchorRef.current.click();
          window.URL.revokeObjectURL(url);
          continue;
        }

        const prevData =
          prevStatusData.find((statusColumn) => statusColumn.type === statusType)
            ?.meetings ?? [];

        gridApiInstance.setRowData(exportRows);
        gridApiInstance.exportDataAsCsv(getParams(statusType));
        gridApiInstance.setRowData(prevData);
      }

      setChecked([]);
    } catch (error) {
      console.log(error);
      toast.error(t("Export failed"));
    } finally {
      setExporting(false);
    }
  }, [checked, status, fetchAllRowsForStatus, getPayloadSearchFields, t]);

  const handleSelectDownload = (type) => {
    setChecked((prevSelected) => {
      const newArray = [...prevSelected];
      if (newArray.includes(type)) {
        return newArray.filter((item) => item !== type);
      }
      newArray.push(type);
      return newArray;
    });
  };

  const sortMeeting = (key) => {
    if (MEETING_STATUS_COLUMNS.includes(key)) {
      setFocusedStatus(key);
    }
    if (meetingKey.includes(key)) {
      let m = meetingKey.filter((ml) => ml !== key);
      setMeetingKey(m);
    } else {
      let m = [...meetingKey];
      m.push(key);
      setMeetingKey(m);
    }
  };

  const handleGlobalSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      runSearchNow();
    }
  };

  const handleGlobalSearchInput = () => {
    scheduleLiveSearch();
  };

  const handleGlobalSearchEvent = (e) => {
    if (!e.target.value?.trim()) {
      runSearchNow();
    }
  };





  const onSelectionChanged = (event, statusType) => {

    const selectedNodes = event.api.getSelectedNodes();
    const selectConsultationId = selectedNodes.map((node) => node.data?.data?.consultation?._id);
    const allSame = selectConsultationId.every(item => item === selectConsultationId[0]);

    const selectedIds = selectedNodes.map((node) => node.data?.data?._id);

    if (MEETING_STATUS_COLUMNS.includes(statusType)) {
      setFocusedStatus(statusType);
    }
    // If selecting from a different status type, clear previous selections from other grids
    if (currentStatus && currentStatus !== statusType) {
      // Clear selections from all other grids
      Object.keys(gridApi).forEach((key) => {
        if (key !== statusType) {
          gridApi[key].deselectAll();
        }
      });
      // Reset and start fresh with new status type
      setCurrentStatus(statusType);
      setDocID(allSame ? selectConsultationId[0] : "");
      setSelectedMeetingIds({
        meetingIds: selectedIds,
      });
    } else {
      // Same status type, update normally
      setDocID(allSame ? selectConsultationId[0] : "");
      setSelectedMeetingIds((prevSelectedIds) => ({
        ...prevSelectedIds,
        meetingIds: selectedIds,
      }));
      setCurrentStatus(statusType);
    }

    // Show/hide bulk actions based on selection
    if (selectedIds.length > 0) {
      setShowAction(true);
    } else {
      setShowAction(false);
      setCurrentStatus(null);
      setDocID("");
    }
  };

  const handleBulkUpdate = async (targetStatus) => {
    const data = {
      statuses: targetStatus,
      meetingIds: selectedMeetingIds.meetingIds,
    };
    try {
      setLoader(true);
      const response = await adminUpdateAppointmentById(data);
      if ([200, 201, 202].includes(response.status)) {
        await refreshMeetings();
        if (response?.data?.message === "Partial success") {
          toast.warning(t("Could not change status of all selected meetings"), {
            toastId: "warning",
          });
        } else {
          toast.success(response?.data?.message, { toastId: "success" });
        }
        clearAllSelections(); // Clear all selections and reset state
        setLoader(false);
      }
    } catch (error) {
      console.log(error);
      clearAllSelections(); // Clear selections even on error
      setLoader(false);
      toast.error(error?.response?.data?.message, { toastId: "error" });
    }
  };

  return (
    <>
      <a ref={anchorRef} style={{ display: "none" }} />
      <ProfileDetails
        detailsopen={detailsopen}
        role={role}
        userid={userid}
        handleDetailsClose={handleDetailsClose}
      />
      <OfferDateModal
        offerModal={offerModal}
        offerMeetingId={offerMeetingId}
        handleCloseModal={handleCloseModal}
        newOfferDate={newOfferDate}
        fdate={fdate}
        setFDate={setFDate}
        sdate={sdate}
        setSDate={setSDate}
        ftime={ftime}
        setFTime={setFTime}
        stime={stime}
        setSTime={setSTime}
        startDate={startDate}
        setStartDate={setStartDate}
        startDate1={startDate1}
        setStartDate1={setStartDate1}
        consultId={consultId}
        offerDates={offerDates}
        mstatus={mstatus}
        requestedDates={reqdates}
        assapValue={assapValue}
      />
      {meetinglogmodal && (
        <MeetingLog
          handleClose={handleMeetingLogModal}
          meetinglogModal={meetinglogmodal}
          meetingId={meetinglogId}
        />
      )}
      <Comment
        comentmeetingid={comentmeetingid}
        comment={comment}
        commentModal={commentModal}
        handleClose={handleCloseComment}
      />
      {id && documentModal && (
        <ViewDocument
          documentModal={documentModal}
          document={document}
          setDocumentModal={setDocumentModal}
          meetingid={id}
          clientId={clientId}
          pacseeDocument={pacseeDocument}
          setPacseeDocument={setPacseeDocument}
          meetingType={meetingType}
        />
      )}
      <MeetingUpdate
        consultfilter={consultfilter}
        updateId={updateId}
        updateData={updateData}
        updateModal={updateModal}
        setUpdateModal={setUpdateModal}
        handleClose={handleClose}
        consultData={consultData}
        priceClose={priceClose}
        priceModal={priceModal}
        setPriceModal={setPriceModal}
        offerDates={offerData}
        doctorId={doctorId}
        setDoctorId={setDoctorId}
        refetchAllmeetings={refreshMeetings}
        allMeetingData={status}
        setUpdateData={setUpdateData}
      />
      <Report
        handleReportModalClose={handleReportModalClose}
        reportModal={reportModal}
      />
      {idfModal && (
        <AdminIdfModal
          idfModal={idfModal}
          handleIdfUpload={handleIdfUpload}
          getallmeetings={refreshMeetings}
        />
      )}
      {myDoctorModal && (
        <MyDoctorUpload
          myDoctorModal={myDoctorModal}
          setMyDoctorModal={setMyDoctorModal}
          handleMyDoctorUpload={handleMyDoctorUpload}
        />
      )}

      <div
        className="adminDataTable"
      // style={{ paddingTop: "13%", background: "#f1f1f1" }}
      >
        <div className="table-header-section">
          <Button
            onClick={() => setReportModal(true)}
            className="download_report"
          >
            Download Report
          </Button>
          <Dropdown>
            <Dropdown.Toggle id="dropdown-basic" className="download_report">
              Export to CSV
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {status.map((statusColumn, index) => {
                const isSelected = checked.includes(statusColumn.type);
                return (
                  <div
                    className="dropdown-item"
                    key={index}
                    onClick={() => handleSelectDownload(statusColumn.type)}
                  >
                    <Form.Check
                      type="checkbox"
                      aria-label={statusColumn.type}
                      label={statusColumn.type}
                      checked={isSelected}
                      readOnly
                    />
                  </div>
                );
              })}
              <div className="download-btn">
                <Button onClick={onExportClick} disabled={exporting}>
                  {exporting ? "Exporting..." : "Download"}
                </Button>
              </div>
            </Dropdown.Menu>
          </Dropdown>
          <Button
            onClick={() => handleIdfUpload()}
            className="download_report min"
            style={{ minWidth: "60px" }}
          >
            IDF
          </Button>
          <Button
            onClick={() => handleMyDoctorUpload()}
            className="download_report"
          >
            myDoctor Upload
          </Button>
          {showAction && (
            <Dropdown>
              <Dropdown.Toggle id="dropdown-basic">Bulk Actions</Dropdown.Toggle>

              <Dropdown.Menu>
                {bulkActionMenu.map((e, i) => {
                  return (
                    <React.Fragment key={i}>
                      {e.id === "moveto" ? (
                        <Dropdown drop="end">
                          <Dropdown.Toggle
                            as="div"
                            className="dropdown-item d-flex justify-content-between align-items-center bulk-action-menu"
                            style={{
                              cursor: "pointer", flexDirection: lang === "EN" ? "row" : "row-reverse"

                            }}
                          >
                            <span>{e.type}</span>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {status.map((statusItem, idx) => (
                              statusItem.type !== currentStatus && (
                                <Dropdown.Item
                                  key={idx}
                                  onClick={() => handleBulkUpdate(statusItem.type)}
                                >
                                  {statusItem.type}
                                </Dropdown.Item>
                              )
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      ) : e.id === "updateDoc" ? (
                        <Dropdown drop="end">
                          <Dropdown.Toggle
                            as="div"
                            className="dropdown-item d-flex justify-content-between align-items-center bulk-action-menu"
                            style={{
                              cursor: doctorList.length > 0 ? "pointer" : "not-allowed", opacity: doctorList.length > 0 ? 1 : 0.6,
                              flexDirection: lang === "EN" ? "row" : "row-reverse"
                            }}
                            disabled={doctorList.length === 0}
                          >
                            <span>{e.type}</span>
                          </Dropdown.Toggle>
                          {doctorList.length > 0 && (
                            <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto" }}>
                              {doctorList.map((doctor, idx) => (
                                <Dropdown.Item
                                  key={idx}
                                  onClick={() =>
                                    handleUpdateDoctor(doctor.doctorId, selectedMeetingIds.meetingIds)
                                  }
                                >
                                  <div>
                                    <div style={{ fontWeight: "500" }}>{doctor.name}</div>
                                    <small style={{ color: "#666" }}>{doctor.specialty}</small>
                                  </div>
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          )}
                        </Dropdown>
                      ) : (
                        <Dropdown.Item className="dropdown-item" as="button">
                          <label>{e.type}</label>
                        </Dropdown.Item>
                      )}

                    </React.Fragment>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          )}
          <span
            className="meetings-total-count"
            style={{ lineHeight: "38px", margin: "0 8px" }}
          >
            {t("Total")}: {counts.totalCount}
          </span>
          <input
            ref={globalSearchRef}
            id="filter-text-box"
            placeholder="Search customer name"
            onChange={handleGlobalSearchInput}
            onInput={handleGlobalSearchInput}
            onSearch={handleGlobalSearchEvent}
            onKeyDown={handleGlobalSearchKeyDown}
            className="global-search"
            type="search"
          />
          <Button
            onClick={() => runSearchNow()}
            className="download_report"
            disabled={countsLoading}
          >
            {isSearchMode && status.some((column) => column.loading)
              ? "Searching..."
              : "Search"}
          </Button>
          <Button
            onClick={handleClearSearch}
            className="download_report"
            disabled={countsLoading}
          >
            Clear search
          </Button>
        </div>

        <Accordion
          defaultActiveKey={status[0].type}
          onSelect={(key) => sortMeeting(key)}
          alwaysOpen
        >
          <div className="accrdion-list-grid">
            {status.map((e, i) => (
              <div className="accrdion-list" key={e.type}>
                <Accordion.Item eventKey={`${e.type}`} key={e.type}>
                  <Accordion.Header
                    onClick={() => {
                      setFocusedStatus(e.type);
                    }}
                  >
                    {e.type} (
                    {hasActiveSearch(
                      appliedSearchRef.current[e.type]?.searchFields,
                      appliedSearchRef.current[e.type]?.search
                    )
                      ? e.total
                      : counts.statusCounts[e.type] ?? 0}
                    )
                  </Accordion.Header>
                  <Accordion.Body>
                    <div
                      className="ag-theme-alpine"
                      style={{ width: "100%", height: 450, position: "relative" }}
                    >
                      {(e.loading || loader) && e.meetings.length === 0 ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ width: "100%", height: "100%" }}
                        >
                          <RotatingLines
                            strokeColor="grey"
                            strokeWidth="5"
                            animationDuration="0.75"
                            width="48"
                            visible={true}
                          />
                        </div>
                      ) : (
                        <>
                          <AgGridReact
                            key={e.type}
                            ref={(el) => (gridRef.current[i] = el)}
                            rowData={e.meetings}
                            gridOptions={{
                              ...getGridOptions(e.type),
                              rowHeight: 60,
                            }}
                            columnDefs={getColumnDefs(currentStatus, e.type)}
                            defaultColDef={defaultColDef}
                            animateRows={true}
                            rowSelection="multiple"
                            onCellClicked={cellClickedListener}
                            alwaysShowHorizontalScroll={true}
                            alwaysShowVerticalScroll={true}
                            onGridReady={(params) => onGridReady(params, e.type)}
                            rowClassRules={rowClassRules}
                            onSelectionChanged={(event) =>
                              onSelectionChanged(event, e.type)
                            }
                            suppressRowClickSelection
                          />
                          {(e.loading || loader) && (
                            <div
                              className="d-flex justify-content-center align-items-center"
                              style={{
                                position: "absolute",
                                inset: 0,
                                backgroundColor: "rgba(255, 255, 255, 0.65)",
                                zIndex: 2,
                              }}
                            >
                              <RotatingLines
                                strokeColor="grey"
                                strokeWidth="5"
                                animationDuration="0.75"
                                width="48"
                                visible={true}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {e.totalPages > 0 && e.page < e.totalPages && (
                      <div
                        className="d-flex justify-content-center align-items-center flex-wrap gap-2 mt-2"
                        dir="ltr"
                      >
                        <Button
                          variant="secondary"
                          onClick={() => handleLoadMore(e.type)}
                          disabled={e.loading}
                        >
                          {e.loading ? t("Loading...") : t("Load more")} (
                          {e.meetings.length}/{e.total})
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={() => handleLoadAll(e.type)}
                          disabled={e.loading}
                        >
                          {e.loading
                            ? t("Loading...")
                            : `${t("Load all")} (${e.total})`}
                        </Button>
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </div>
            ))}
          </div>
        </Accordion>
      </div>
    </>
  );
};

export default MeetingDetails;

export function ageCalculator(d) {
  var dob = new Date(d);
  var month_diff = Date.now() - dob.getTime();
  var age_dt = new Date(month_diff);
  var year = age_dt.getUTCFullYear();
  var age = Math.abs(year - 1970);
  return age;
}
